import { Grid, Paper } from "@mui/material";
import TitledCard from "../basic/TitledCard";
import { useIntl } from "react-intl";
import { useLocale } from "../../../locales/LocaleProvider";
import { useCallback, useEffect } from "react";
import Separator from "./elements/Separator";
import { useDevice } from "../../../utils/DeviceProvider";
import { useServerCommunication } from "../../../utils/ServerCommunicaton";
import { TemplateBlockRepeater } from "./TemplateBlockRepeater";
import { useUserInfo } from "../../../utils/user-info";
import { TemplateRootItem } from "@helios/toolbox-types";

interface TemplateBlocksProp {
  configKey: string;
  blocks: TemplateRootItem[];
  endUserKeys?: string[];
}

const TemplateBlocks = ({ configKey, blocks, endUserKeys }: TemplateBlocksProp) => {
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const {
    device: { currentValue, unitid },
    setCurrentValue,
    setEmptyConfig,
  } = useDevice();
  const { getDefaultUnitConfig } = useServerCommunication();
  const [user] = useUserInfo();
  const limitedAccessUser = user?.rights.limited_user;

  const getTitle = useCallback(
    (title: string | { [locale: string]: string } | null) => {
      if (!title) {
        return formatMessage({ defaultMessage: "General configuration" });
      } else if (typeof title === "string") {
        return title;
      } else {
        return title[locale] ?? title.en ?? title[Object.keys(title)[0]];
      }
    },
    [formatMessage, locale]
  );

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    if (!currentValue[configKey] || !currentValue[configKey].items) {
      getDefaultUnitConfig(unitid).then((config) => {
        currentValue[configKey] = config[configKey];
        setCurrentValue(currentValue);
        setEmptyConfig(config[configKey], configKey);
      });
    }
  }, [configKey, currentValue, getDefaultUnitConfig, setCurrentValue, setEmptyConfig, unitid]);

  return (
    <Grid container spacing={1}>
      {blocks.map(({ title, items, repeat }, i) => (
        <Grid
          item
          xs={12}
          key={`template-block-${i}`}
          sx={(limitedAccessUser && endUserKeys) && items.filter((i) => i.key && endUserKeys?.includes(`${i.key}`)).length === 0 ? { display: "none" } : undefined}
        >
          {title && items.length === 0 ? (
            <Separator text={getTitle(title)} headline />
          ) : title || i === 0 ? (
            <TitledCard title={getTitle(title)}>
              <TemplateBlockRepeater repeat={repeat} configKey={configKey} items={items} endUserKeys={endUserKeys} />
            </TitledCard>
          ) : (
            <Paper sx={{ display: "flex" }}>
              <TemplateBlockRepeater repeat={repeat} configKey={configKey} items={items} endUserKeys={endUserKeys} />
            </Paper>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export default TemplateBlocks;
